import log from '@uwm/logger';

import UamfHttpError from './errors/UamfHttpError';
import type {HttpMethod, UamfRequestParams} from './types';

const API_KEY_HEADER = 'x-api-key';

const applyPathParams = (path: string, pathParams: (string | number)[] = []): string =>
    pathParams.reduce<string>((result, param, index) => result.replace(`{${index}}`, String(param)), path);

const buildUrl = (
    endpoint: string,
    path: string,
    pathParams?: (string | number)[],
    queryParams: UamfRequestParams['queryParams'] = {},
): string => {
    const url = new URL(endpoint);
    const resolvedPath = applyPathParams(path, pathParams).replace(/^\//, '');

    url.pathname = `${url.pathname.replace(/\/$/, '')}/${resolvedPath}`;

    for (const [key, value] of Object.entries(queryParams ?? {})) {
        if (value !== undefined && value !== null) url.searchParams.set(key, String(value));
    }

    return url.toString();
};

const createHeaders = (
    token?: string,
    apiKey?: string,
    extraHeaders?: Record<string, string>,
): Record<string, string> => {
    const headers: Record<string, string> = {'Content-Type': 'application/json', ...extraHeaders};

    if (token) headers.Authorization = `Bearer ${token}`;
    if (apiKey) headers[API_KEY_HEADER] = apiKey;

    return headers;
};

const request = async (method: HttpMethod, params: UamfRequestParams): Promise<unknown> => {
    const {endpoint, path, pathParams, queryParams, body, token, apiKey, headers, isThrowError, mapping} = params;
    let url: string | undefined;

    try {
        url = buildUrl(endpoint, path, pathParams, queryParams);

        log.info(`uamfClient: Request started. Method: ${method}. Url: ${url}`);

        const response = await fetch(url, {
            method,
            headers: createHeaders(token, apiKey, headers),
            body: body === undefined ? undefined : JSON.stringify(body),
        });

        if (!response.ok) {
            const errorBody = await response.json().catch(() => undefined);

            throw new UamfHttpError(response.status, response.statusText, errorBody);
        }

        const data = await response.json();

        log.debug(`uamfClient: Response received. Url: ${url}`);

        return mapping ? mapping(data) : data;
    } catch (e) {
        log.error(`uamfClient: Request failed. Url: ${url ?? endpoint}`, (e as Error).message);

        if (isThrowError) throw e;

        return null;
    }
};

const callGet = (params: UamfRequestParams) => request('GET', params);
const callPost = (params: UamfRequestParams) => request('POST', params);

export default {callGet, callPost};
